import { useEffect, useMemo, useRef, useState } from "react";
import { benchmarks, labs, models, prices, sources } from "../data/v1";
import type { BenchmarkId } from "../types";
import { Icon } from "./Icons";

export type SearchSelection =
  | { kind: "model"; id: string }
  | { kind: "benchmark"; id: BenchmarkId }
  | { kind: "source"; id: string }
  | { kind: "pricing"; id: string };

interface SearchItem {
  key: string;
  label: string;
  meta: string;
  group: "Models" | "Benchmarks" | "Sources" | "Pricing";
  selection: SearchSelection;
}

interface SearchDialogProps {
  open: boolean;
  onClose: () => void;
  onSelect: (selection: SearchSelection) => void;
}

export function SearchDialog({ open, onClose, onSelect }: SearchDialogProps) {
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const items = useMemo<SearchItem[]>(() => {
    const modelItems = models.map((model) => ({
      key: `model-${model.id}`,
      label: model.name,
      meta: labs.find((lab) => lab.id === model.ownerId)?.name ?? model.ownerId,
      group: "Models" as const,
      selection: { kind: "model" as const, id: model.id },
    }));
    const benchmarkItems = benchmarks.map((benchmark) => ({
      key: `benchmark-${benchmark.id}`,
      label: benchmark.name,
      meta: `${benchmark.category} · ${benchmark.metric}`,
      group: "Benchmarks" as const,
      selection: { kind: "benchmark" as const, id: benchmark.id },
    }));
    const sourceItems = sources
      .filter((source) => source.kind === "Release post")
      .map((source) => ({
        key: `source-${source.id}`,
        label: source.title,
        meta: labs.find((lab) => lab.id === source.publisherId)?.name ?? source.publisherId,
        group: "Sources" as const,
        selection: { kind: "source" as const, id: source.id },
      }));
    const pricingItems = prices.map((price) => {
      const model = models.find((candidate) => candidate.id === price.modelId);
      return {
        key: `price-${price.modelId}`,
        label: `${model?.name ?? price.modelId} pricing`,
        meta: `$${price.input} in · $${price.output} out / 1M`,
        group: "Pricing" as const,
        selection: { kind: "pricing" as const, id: price.modelId },
      };
    });
    return [...modelItems, ...benchmarkItems, ...sourceItems, ...pricingItems];
  }, []);

  const filtered = useMemo(() => {
    const normalized = query.trim().toLocaleLowerCase();
    if (!normalized) return items.slice(0, 12);
    return items
      .filter((item) => `${item.label} ${item.meta} ${item.group}`.toLocaleLowerCase().includes(normalized))
      .slice(0, 18);
  }, [items, query]);

  useEffect(() => {
    if (open) {
      setQuery("");
      setActiveIndex(0);
      window.setTimeout(() => inputRef.current?.focus(), 20);
      document.body.dataset.modalOpen = "true";
    } else {
      delete document.body.dataset.modalOpen;
    }
    return () => {
      delete document.body.dataset.modalOpen;
    };
  }, [open]);

  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  if (!open) return null;

  const choose = (item: SearchItem) => {
    onSelect(item.selection);
    onClose();
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Escape") {
      event.preventDefault();
      onClose();
      return;
    }
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex((current) => Math.min(filtered.length - 1, current + 1));
      return;
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((current) => Math.max(0, current - 1));
      return;
    }
    if (event.key === "Enter" && filtered[activeIndex]) {
      event.preventDefault();
      choose(filtered[activeIndex]);
      return;
    }
    if (event.key === "Tab") {
      const focusable = panelRef.current?.querySelectorAll<HTMLElement>(
        "input, button:not([disabled]), a[href]",
      );
      if (!focusable?.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  };

  return (
    <div className="command-dialog" role="presentation" onKeyDown={handleKeyDown}>
      <button className="command-dialog__backdrop" aria-label="Close search" onClick={onClose} />
      <div
        aria-label="Search models, benchmarks, sources, and prices"
        aria-modal="true"
        className="command-dialog__panel"
        ref={panelRef}
        role="dialog"
      >
        <div className="command-dialog__field">
          <Icon name="search" size={20} />
          <input
            aria-controls="command-results"
            aria-label="Search ModelScout"
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search models, benchmarks, sources…"
            ref={inputRef}
            value={query}
          />
          <button className="icon-button" aria-label="Close search" onClick={onClose}>
            <Icon name="close" />
          </button>
        </div>
        <div className="command-dialog__results" id="command-results" role="listbox">
          {filtered.length ? (
            filtered.map((item, index) => (
              <button
                aria-selected={activeIndex === index}
                className="command-result"
                key={item.key}
                onClick={() => choose(item)}
                onMouseEnter={() => setActiveIndex(index)}
                role="option"
              >
                <span>
                  <strong>{item.label}</strong>
                  <small>{item.meta}</small>
                </span>
                <span className="command-result__group">{item.group}</span>
              </button>
            ))
          ) : (
            <p className="empty-state">No source-backed record matches “{query}”.</p>
          )}
        </div>
        <div className="command-dialog__footer" aria-hidden="true">
          <span><kbd>↑</kbd><kbd>↓</kbd> navigate</span>
          <span><kbd>Enter</kbd> open</span>
          <span><kbd>Esc</kbd> close</span>
        </div>
      </div>
    </div>
  );
}
